import { useMemo } from "react"

import type { MetricRecordData } from "@/types/metrics/contracts"

export type AggregationType =
  | "SUM"
  | "AVERAGE"
  | "COUNT"
  | "COUNT_WHERE"
  | "MIN"
  | "MAX"

export type DerivedMetricType = "strict_logic" | "aggregate"

const SEVERITY_QUALITY_UNITS = new Set(["severity", "quality"])

interface EligibilityResult {
  eligible: boolean
  reason?: string
}

const UNRESOLVED_PARENT_REASON =
  "A previously selected parent could not be loaded — refresh or remove it before adding more parents"

/**
 * Determine whether a given metric is eligible as a parent for the given
 * derived metric configuration. Returns `{ eligible: true }` or
 * `{ eligible: false, reason: "..." }`.
 *
 * Self-reference is filtered out by `useEligibleParentMetrics` before this
 * function runs; it is not re-checked here.
 */
export function getEligibilityForParent(
  candidate: MetricRecordData,
  opts: {
    targetMetricType: DerivedMetricType
    aggregationType?: AggregationType
    alreadySelectedUnits: string[]
    hasUnresolvedSelectedParents?: boolean
  },
): EligibilityResult {
  const {
    targetMetricType,
    aggregationType,
    alreadySelectedUnits,
    hasUnresolvedSelectedParents,
  } = opts

  if (candidate.is_llm_judge === true) {
    return {
      eligible: false,
      reason: "LLM judge metric — not allowed as a composite parent",
    }
  }

  if (hasUnresolvedSelectedParents) {
    return {
      eligible: false,
      reason: UNRESOLVED_PARENT_REASON,
    }
  }

  if (targetMetricType === "strict_logic") {
    return { eligible: true }
  }

  // Aggregate rules
  const outputType = candidate.output_type ?? "string"
  const unit = candidate.metric_metadata?.units ?? undefined

  switch (aggregationType) {
    case "SUM":
    case "AVERAGE":
    case "MIN":
    case "MAX": {
      if (outputType !== "float") {
        return {
          eligible: false,
          reason: `${aggregationType} requires float output — this metric outputs ${outputType}`,
        }
      }
      if (
        aggregationType === "SUM" &&
        unit &&
        SEVERITY_QUALITY_UNITS.has(unit)
      ) {
        return {
          eligible: false,
          reason: `SUM is disallowed for ${unit.toUpperCase()} parents — pick AVERAGE, MIN, or MAX or a different parent`,
        }
      }
      if (alreadySelectedUnits.length > 0) {
        const definedSelected = alreadySelectedUnits.filter(Boolean)
        if (definedSelected.length > 0) {
          if (!unit) {
            return {
              eligible: false,
              reason: `Unit mismatch — selected parents use unit "${definedSelected[0]}", this metric has no unit`,
            }
          }
          if (!definedSelected.includes(unit)) {
            return {
              eligible: false,
              reason: `Unit mismatch — selected parents use unit "${definedSelected[0]}", this metric is "${unit}"`,
            }
          }
        } else if (unit) {
          return {
            eligible: false,
            reason: `Unit mismatch — selected parents have no unit, this metric is "${unit}"`,
          }
        }
      }
      return { eligible: true }
    }

    case "COUNT": {
      if (outputType !== "float" && outputType !== "string") {
        return {
          eligible: false,
          reason: `COUNT requires float or string output — this metric outputs ${outputType}`,
        }
      }
      return { eligible: true }
    }

    case "COUNT_WHERE": {
      if (outputType !== "string") {
        return {
          eligible: false,
          reason: `COUNT_WHERE requires string output — this metric outputs ${outputType}`,
        }
      }
      return { eligible: true }
    }

    case undefined:
    default:
      return { eligible: true }
  }
}

interface EligibleParentEntry {
  metric: MetricRecordData
  eligibility: EligibilityResult
}

interface UseEligibleParentMetricsInput {
  metrics: MetricRecordData[]
  targetMetricType: DerivedMetricType
  aggregationType?: AggregationType
  alreadySelectedParentIds?: string[]
  selfId?: string
}

/**
 * Filter and sort org metrics for the derived-metric parent picker.
 * Returns eligible metrics first (alphabetical), then ineligible (alphabetical).
 *
 * `unresolvedSelectedParentIds` lists any IDs from `alreadySelectedParentIds`
 * that could not be resolved against the current `metrics` array (deleted,
 * paginated out, etc.). When non-empty, every candidate is marked ineligible
 * so unit enforcement does not silently weaken — callers should surface the
 * unresolved IDs to the user so they can identify and remove them.
 */
export function useEligibleParentMetrics({
  metrics,
  targetMetricType,
  aggregationType,
  alreadySelectedParentIds = [],
  selfId,
}: UseEligibleParentMetricsInput): {
  entries: EligibleParentEntry[]
  eligibleCount: number
  ineligibleCount: number
  unresolvedSelectedParentIds: string[]
} {
  return useMemo(() => {
    const metricsById = new Map(
      metrics.filter((m) => m.id).map((m) => [m.id as string, m]),
    )

    const unresolvedSelectedParentIds = alreadySelectedParentIds.filter(
      (id) => !metricsById.has(id),
    )
    const hasUnresolvedSelectedParents = unresolvedSelectedParentIds.length > 0

    const alreadySelectedUnits = alreadySelectedParentIds
      .map((id) => metricsById.get(id)?.metric_metadata?.units ?? "")
      .filter(Boolean)

    const entries: EligibleParentEntry[] = metrics
      .filter((m) => m.id !== selfId)
      .map((m) => ({
        metric: m,
        eligibility: getEligibilityForParent(m, {
          targetMetricType,
          aggregationType,
          alreadySelectedUnits,
          hasUnresolvedSelectedParents,
        }),
      }))

    const eligible = entries
      .filter((e) => e.eligibility.eligible)
      .sort((a, b) =>
        (a.metric.metric_display_name ?? "").localeCompare(
          b.metric.metric_display_name ?? "",
        ),
      )

    const ineligible = entries
      .filter((e) => !e.eligibility.eligible)
      .sort((a, b) =>
        (a.metric.metric_display_name ?? "").localeCompare(
          b.metric.metric_display_name ?? "",
        ),
      )

    return {
      entries: [...eligible, ...ineligible],
      eligibleCount: eligible.length,
      ineligibleCount: ineligible.length,
      unresolvedSelectedParentIds,
    }
  }, [
    metrics,
    targetMetricType,
    aggregationType,
    alreadySelectedParentIds,
    selfId,
  ])
}
